/**
 * Annuity purchase section on the Strategy step (step 9).
 *
 * One section per person: a toggle, then age, amount, tax-free cash, income
 * type, joint life and the rate. Shared by the desktop and mobile forms so the
 * ids, copy and rate auto-fill behave identically on both — `variant` only
 * swaps the class names.
 */

import { useEffect, useState } from 'react';
import type { AnnuityEscalation, AnnuityPurchaseData, PersonData } from '../../types/wizard';
import {
  ANNUITY_DEFAULT_ESCALATION_PCT,
  ANNUITY_DEFAULT_SURVIVOR_PCT,
  ANNUITY_ESCALATION_LABELS,
  ANNUITY_MPAA_NOTE,
  ANNUITY_IRREVERSIBLE_NOTE,
  minimumAnnuityPurchaseAge,
  ANNUITY_RPI_WEDGE_NOTE,
  annuityRateCaption,
  makeDefaultAnnuityPurchase,
  withRefreshedRate,
} from './annuityPurchaseHelpers';

interface AnnuityPurchaseSectionProps {
  personIndex: number;
  person?: Pick<PersonData, 'fullName' | 'dateOfBirth' | 'retirementDate'>;
  /** Show the person's name above the fields (joint plans only). */
  showPersonName: boolean;
  isJointPlan: boolean;
  purchase?: AnnuityPurchaseData;
  /** How many purchases this person actually has; the wizard edits the first. */
  purchaseCount?: number;
  onChange: (personIndex: number, purchase: AnnuityPurchaseData | null) => void;
  /** Reports whether this section currently holds an entry the step must not advance past. */
  onValidityChange?: (personIndex: number, blocked: boolean) => void;
  variant?: 'desktop' | 'mobile';
}

/** Number inputs render '' rather than NaN so the field can be cleared and retyped. */
function numberValue(value: number | undefined): number | string {
  return Number.isFinite(value) ? (value as number) : '';
}

/**
 * Parse a number field, returning undefined for anything not finite.
 *
 * A cleared box parses to NaN, and the Strategy step's autosave writes the raw
 * form value straight to the draft without going through the Zod resolver — so
 * an unguarded NaN persists and serialises to null, which the projection
 * schema rejects (optional numbers are omitted, never null).
 */
function parsedNumber(raw: string, decimals = false): number | undefined {
  const value = decimals ? Number.parseFloat(raw) : Number.parseInt(raw, 10);
  return Number.isFinite(value) ? value : undefined;
}

export function AnnuityPurchaseSection({
  personIndex, person, showPersonName, isJointPlan, purchase, purchaseCount, onChange,
  onValidityChange, variant = 'desktop',
}: Readonly<AnnuityPurchaseSectionProps>) {
  // The rate is held as raw text while being typed so a decimal point survives
  // the round trip through a parsed number (typing "8.1" would otherwise lose
  // the "." the moment the value re-renders as 8).
  const [rateDraft, setRateDraft] = useState<string | null>(null);
  // Number boxes the user has emptied. The last good value stays in form state
  // so nothing invalid is ever saved, while the box renders blank for retyping.
  const [blankFields, setBlankFields] = useState<Set<string>>(new Set());
  // An age below the UK minimum pension age is shown back to the user with an
  // error, but deliberately NOT written to form state: the Strategy step's
  // autosave bypasses the Zod resolver, so a value that reaches state reaches
  // the draft and then fails as a server error at projection time.
  const [rejectedAge, setRejectedAge] = useState<string | null>(null);

  const mobile = variant === 'mobile';
  const cls = {
    group: mobile ? 'mobile-field' : 'form-group',
    label: mobile ? 'mobile-label' : 'form-label',
    input: mobile ? 'mobile-input' : 'form-input',
    select: mobile ? 'mobile-input' : 'form-input',
    checkbox: mobile ? 'auth-mobile__checkbox' : 'annuity-checkbox',
    help: mobile ? 'mobile-help-text' : 'help-text',
  };

  // 55 or 57 by date of birth — the same rule the server enforces, so a bad
  // age is caught inline rather than at projection time.
  const minimumAge = minimumAnnuityPurchaseAge(person);
  // Two ways to be invalid, and both must block:
  //  - the user just typed one (held out of state, so only `rejectedAge` knows);
  //  - one is ALREADY in state, saved when the date of birth made it legal and
  //    left behind when that date changed on step 1. Deriving only from
  //    rejections missed the second entirely: no error, button enabled, and a
  //    generic incomplete-plan failure at projection time.
  const storedAgeBelowMinimum = purchase != null
    && Number.isFinite(purchase.purchaseAge)
    && purchase.purchaseAge < minimumAge;
  const belowMinimumAge = rejectedAge != null || storedAgeBelowMinimum;

  useEffect(() => {
    onValidityChange?.(personIndex, belowMinimumAge);
    // Report clear on unmount, so a section that goes away (or a person
    // removed from the plan) cannot leave the step blocked for ever.
    return () => onValidityChange?.(personIndex, false);
  }, [onValidityChange, personIndex, belowMinimumAge]);

  const enabled = purchase != null;
  const update = (changes: Partial<AnnuityPurchaseData>, refreshRate = false): void => {
    if (!purchase) return;
    const next = { ...purchase, ...changes };
    onChange(personIndex, refreshRate ? withRefreshedRate(next) : next);
  };

  /**
   * Apply a numeric edit, but hold the save back while the box is empty: the
   * field is required, and persisting a half-typed row would write an
   * incomplete purchase to the draft. The local draft state keeps the box
   * visually cleared so the user can retype.
   */
  const updateNumber = (
    key: 'purchaseAge' | 'amountPct' | 'amountFixed' | 'survivorPct',
    raw: string,
    refreshRate = false,
  ): void => {
    const value = parsedNumber(raw);
    setBlankFields(prev => {
      const next = new Set(prev);
      if (value === undefined) next.add(key); else next.delete(key);
      return next;
    });
    if (value === undefined) return;
    update({ [key]: value } as Partial<AnnuityPurchaseData>, refreshRate);
  };

  /** Renders '' for a box the user has cleared, even though state keeps the last good value. */
  const fieldValue = (key: 'purchaseAge' | 'amountPct' | 'amountFixed' | 'survivorPct'): number | string =>
    blankFields.has(key) ? '' : numberValue(purchase?.[key]);

  const toggle = (checked: boolean): void => {
    setRateDraft(null);
    // Switching off removes the field AND its error markup, so a rejection
    // left standing here would block the step with nothing on screen to
    // explain it.
    setRejectedAge(null);
    onChange(personIndex, checked ? makeDefaultAnnuityPurchase(person) : null);
  };

  const changeEscalation = (escalation: AnnuityEscalation): void => {
    setRateDraft(null);
    update({
      escalation,
      escalationPct: escalation === 'FIXED' ? (purchase?.escalationPct ?? ANNUITY_DEFAULT_ESCALATION_PCT) : undefined,
    }, true);
  };

  const changeJointLife = (jointLife: boolean): void => {
    setRateDraft(null);
    update({
      jointLife,
      survivorPct: jointLife ? (purchase?.survivorPct ?? ANNUITY_DEFAULT_SURVIVOR_PCT) : undefined,
    }, true);
  };

  const changeAmountMode = (amountMode: 'PERCENT' | 'FIXED'): void => {
    update(amountMode === 'PERCENT'
      ? { amountMode, amountPct: purchase?.amountPct ?? 25, amountFixed: undefined }
      : { amountMode, amountFixed: purchase?.amountFixed, amountPct: undefined });
  };

  return (
    <div className="annuity-person" data-testid={`annuity-section-person${personIndex}`}>
      {showPersonName && (
        <div className="annuity-person-name">{person?.fullName || `Person ${personIndex + 1}`}</div>
      )}

      <label className={cls.checkbox} htmlFor={`annuity-enabled-${personIndex}`}>
        <input
          id={`annuity-enabled-${personIndex}`}
          type="checkbox"
          checked={enabled}
          onChange={(e) => toggle(e.target.checked)}
        />
        <span>Buy an annuity with part of the pension</span>
      </label>

      {(purchaseCount ?? 0) > 1 && (
        <p className={`${cls.help} annuity-extra-purchases`} role="note">
          This plan has {purchaseCount} annuity purchases for this person. The wizard shows
          only the first; the others are kept as they are and still appear in the projection.
        </p>
      )}

      {enabled && purchase && (
        <div className="annuity-fields">
          <div className={cls.group}>
            <label htmlFor={`annuity-age-${personIndex}`} className={cls.label}>Age to buy the annuity</label>
            <input
              id={`annuity-age-${personIndex}`}
              type="number"
              min={minimumAge}
              max={100}
              className={cls.input}
              value={rejectedAge ?? fieldValue('purchaseAge')}
              onChange={(e) => {
                setRateDraft(null);
                const raw = e.target.value;
                const parsed = parsedNumber(raw);
                // Show it back, warn, but keep it out of the draft.
                if (parsed !== undefined && parsed < minimumAge) {
                  setRejectedAge(raw);
                  return;
                }
                setRejectedAge(null);
                updateNumber('purchaseAge', raw, true);
              }}
            />
            {belowMinimumAge && (
              <span className="error-message" role="alert">
                A pension cannot be touched before the UK minimum pension age of {minimumAge} for this date of birth.
              </span>
            )}
            <span className={cls.help}>Bought in the year you reach this age, from the pension pot.</span>
          </div>

          <div className={cls.group}>
            <label htmlFor={`annuity-mode-${personIndex}`} className={cls.label}>How much to use</label>
            <select
              id={`annuity-mode-${personIndex}`}
              className={cls.select}
              value={purchase.amountMode}
              onChange={(e) => changeAmountMode(e.target.value as 'PERCENT' | 'FIXED')}
            >
              <option value="PERCENT">Percentage of the pension</option>
              <option value="FIXED">A fixed amount</option>
            </select>
          </div>

          {purchase.amountMode === 'PERCENT' ? (
            <div className={cls.group}>
              <label htmlFor={`annuity-pct-${personIndex}`} className={cls.label}>% of pension to use</label>
              <input
                id={`annuity-pct-${personIndex}`}
                type="number"
                min={1}
                max={100}
                className={cls.input}
                value={fieldValue('amountPct')}
                onChange={(e) => updateNumber('amountPct', e.target.value)}
              />
            </div>
          ) : (
            <div className={cls.group}>
              <label htmlFor={`annuity-amount-${personIndex}`} className={cls.label}>
                Amount to take from the pension (£, today&rsquo;s money)
              </label>
              <input
                id={`annuity-amount-${personIndex}`}
                type="number"
                min={0}
                className={cls.input}
                value={fieldValue('amountFixed')}
                onChange={(e) => updateNumber('amountFixed', e.target.value)}
              />
              {purchase.takeTaxFreeCash && (
                <span className={cls.help}>
                  A quarter of this is paid to you as tax-free cash; the rest buys the annuity income.
                </span>
              )}
            </div>
          )}

          <label className={cls.checkbox} htmlFor={`annuity-tfc-${personIndex}`}>
            <input
              id={`annuity-tfc-${personIndex}`}
              type="checkbox"
              checked={purchase.takeTaxFreeCash}
              onChange={(e) => update({ takeTaxFreeCash: e.target.checked })}
            />
            <span>Take 25% tax-free cash at purchase</span>
          </label>

          <div className={cls.group}>
            <label htmlFor={`annuity-type-${personIndex}`} className={cls.label}>Income type</label>
            <select
              id={`annuity-type-${personIndex}`}
              className={cls.select}
              value={purchase.escalation}
              onChange={(e) => changeEscalation(e.target.value as AnnuityEscalation)}
            >
              {(Object.keys(ANNUITY_ESCALATION_LABELS) as AnnuityEscalation[]).map((key) => (
                <option key={key} value={key}>{ANNUITY_ESCALATION_LABELS[key]}</option>
              ))}
            </select>
            {purchase.escalation === 'RPI' && (
              <span className={cls.help}>{ANNUITY_RPI_WEDGE_NOTE}</span>
            )}
          </div>

          {isJointPlan && (
            <label className={cls.checkbox} htmlFor={`annuity-joint-${personIndex}`}>
              <input
                id={`annuity-joint-${personIndex}`}
                type="checkbox"
                checked={purchase.jointLife}
                onChange={(e) => changeJointLife(e.target.checked)}
              />
              <span>Joint life (income continues to your partner)</span>
            </label>
          )}

          {isJointPlan && purchase.jointLife && (
            <div className={cls.group}>
              <label htmlFor={`annuity-survivor-${personIndex}`} className={cls.label}>Survivor gets (%)</label>
              <input
                id={`annuity-survivor-${personIndex}`}
                type="number"
                min={0}
                max={100}
                className={cls.input}
                value={fieldValue('survivorPct')}
                onChange={(e) => updateNumber('survivorPct', e.target.value)}
              />
            </div>
          )}

          <div className={cls.group}>
            <label htmlFor={`annuity-rate-${personIndex}`} className={cls.label}>Annual income per £100 bought</label>
            <input
              id={`annuity-rate-${personIndex}`}
              type="number"
              step="0.01"
              min={0.5}
              max={20}
              className={cls.input}
              value={rateDraft ?? numberValue(purchase.annualRatePct)}
              onChange={(e) => {
                const raw = e.target.value;
                setRateDraft(raw);
                const parsed = parsedNumber(raw, true);
                // Typing a rate marks it as the user's own quote, so it is no
                // longer re-filled when the age or income type changes. An
                // empty box holds the last good rate rather than saving NaN.
                if (parsed === undefined) return;
                update({ annualRatePct: parsed, rateEdited: true });
              }}
              onBlur={() => setRateDraft(null)}
            />
            <span className={cls.help}>{annuityRateCaption(purchase)}</span>
          </div>

          <p className={cls.help}>{ANNUITY_IRREVERSIBLE_NOTE}</p>
          <p className={cls.help}>{ANNUITY_MPAA_NOTE}</p>
        </div>
      )}
    </div>
  );
}

export default AnnuityPurchaseSection;
